import { requestUploadRelayTicket } from '../api/uploadRelay';
import type { LocalUploadRoute, RelayReply } from '../types/uploadRelay';

/** In-memory only. No Firebase bearer reaches the relay or Google; renew before new requests. */
export function createUploadRelayLease(route: LocalUploadRoute = 'direct') {
  let current: { sessionId: string; reply: RelayReply } | undefined;
  async function destination(
    sessionId: string,
    directUrl: string,
    signal?: AbortSignal,
  ): Promise<{ url: string; headers: Record<string, string> }> {
    if (route === 'direct') return { url: directUrl, headers: {} };
    if (route !== 'china') throw new Error('upload_route_invalid');
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    if (current?.sessionId !== sessionId || current.reply.expiresAt * 1000 <= Date.now() + 60_000) {
      // Each upload is sequential. Do not share an in-flight issuance with an expired-session
      // replacement: that would attach the old session's ticket to a new backend id.
      const reply = await requestUploadRelayTicket(
        sessionId,
        signal,
        current?.sessionId === sessionId ? current.reply.uploads[0]?.ticket : undefined,
      );
      current = { sessionId, reply };
    }
    const lease = current.reply.uploads[0];
    if (!lease) throw new Error('upload_relay_unavailable');
    return { url: lease.url, headers: { 'X-Upload-Relay-Ticket': lease.ticket } };
  }
  return {
    destination,
    invalidate: () => {
      if (current) current.reply.expiresAt = 0;
    },
  };
}

/** A new signed frame plan per retry; no browser-supplied upstream URL enters the backend. */
export async function uploadRelayFrames(
  sessionId: string,
  frames: readonly Blob[],
  signal?: AbortSignal,
) {
  const reply = await requestUploadRelayTicket(sessionId, signal, undefined, frames.length);
  await Promise.all(
    reply.uploads.map(async (lease) => {
      const frame = frames[lease.index];
      if (!frame) throw new Error('upload_relay_frame_mismatch');
      const response = await fetch(lease.url, {
        method: 'PUT',
        body: frame,
        signal,
        credentials: 'omit',
        redirect: 'error',
        headers: {
          'Content-Type': 'image/jpeg',
          'X-Upload-Relay-Ticket': lease.ticket,
        },
      });
      if (!response.ok) throw new Error('upload_relay_frame_failed');
    }),
  );
}
